import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Timezone } from "@syncfusion/ej2-angular-schedule";
import { combineLatest, from, merge, Observable, of, Subject } from "rxjs";
import { catchError, filter, map, shareReplay, startWith, switchMap, take, tap } from "rxjs/operators";
import { LocalStorageService } from "../local-storage.service";
import { MainDataModels } from "../models/maindata-models";
import { SwissAppConfigModels } from "../models/swissappconfig-models";
import { DatumFormatPipe } from "../pipes/datum-format.pipe";
import { HelperService } from "./helper.service";
import { UserAuthenticationService } from "./user-authentication.service";

const STORAGE_DB = "SwissWebMainDB";
const KEY_CALENDAR = "kalender";
const KEY_EMPLOYEES = "mitarbeiter";
const KEY_SELECTED_EMPLOYEES = "last_selected_schedule_employees";
const KEY_LAST_APPOINTMENT = "last_used_appointment";

/**
 * Reactive data access for the calendar (pendenzen) view. Replaces the calendar
 * related members of {@link MainDataService} with observable streams and subjects.
 */
@Injectable({ providedIn: "root" })
export class CalendarService {
    private readonly http = inject(HttpClient);
    private readonly helper = inject(HelperService);
    private readonly auth = inject(UserAuthenticationService);
    private readonly storage = inject(LocalStorageService);
    private readonly dateFormatPipe = inject(DatumFormatPipe);
    private readonly timezone = new Timezone();

    private readonly config$: Observable<SwissAppConfigModels.WebServiceConfig> = this.helper.getSwissWebServiceConfig$().pipe(
        filter((config) => !!config?.termUrl),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    private readonly reloadSubject = new Subject<void>();
    private readonly reload$: Observable<void> = this.reloadSubject.asObservable();

    private readonly selectionSubject = new Subject<string[]>();
    /** The employees whose appointments are currently shown, starting from the persisted selection. */
    readonly selectedEmployees$: Observable<string[]> = merge(this.loadStoredSelection(), this.selectionSubject).pipe(shareReplay({ bufferSize: 1, refCount: true }));

    private triggerReload(): void {
        this.reloadSubject.next();
    }

    setSelectedEmployees(employees: string[]): void {
        this.storage.add(KEY_SELECTED_EMPLOYEES, employees, STORAGE_DB);
        this.selectionSubject.next(employees);
    }

    getEmployees(): Observable<MainDataModels.Mitarbeiter[]> {
        return this.config$.pipe(
            take(1),
            switchMap((config) => this.http.get<MainDataModels.Mitarbeiter[]>(`${config.userUrl}/Mitarbeiter`, this.httpOptions)),
            tap((employees) => this.storage.add(KEY_EMPLOYEES, employees, STORAGE_DB)),
            catchError((err) =>
                of(err).pipe(
                    tap((error) => this.logError("getEmployees", error)),
                    switchMap(() => this.cached<MainDataModels.Mitarbeiter[]>(KEY_EMPLOYEES, [])),
                ),
            ),
            map((employees) => [...employees].sort((a, b) => a.loginname.toLocaleLowerCase().localeCompare(b.loginname.toLocaleLowerCase()))),
        );
    }

    getCalendarData(employees: string[]): Observable<MainDataModels.KalenderDaten | undefined> {
        return combineLatest([this.config$, this.reload$.pipe(startWith(undefined))]).pipe(
            map(([config]) => config),
            map((config) => ({ config, params: new HttpParams().set("mitarbeiter", employees.join(", ")) })),
            switchMap(({ config, params }) => this.http.get<MainDataModels.KalenderDaten>(`${config.termUrl}/MitarbeiterKalenderData`, { params })),
            tap((data) => this.storage.add(KEY_CALENDAR, data, STORAGE_DB)),
            catchError((err) =>
                of(err).pipe(
                    tap((error) => this.logError("getCalendarData", error)),
                    switchMap(() => this.cached<MainDataModels.KalenderDaten | undefined>(KEY_CALENDAR, undefined)),
                ),
            ),
        );
    }

    getAppointmentById(calendarId: string): Observable<MainDataModels.Termin> {
        return this.config$.pipe(
            take(1),
            switchMap((config) => this.http.get<MainDataModels.Termin>(`${config.termUrl}/TerminByID?CalendarID=${calendarId}`, this.httpOptions)),
        );
    }

    loadAppointmentWithUsers(calendarId: string): Observable<MainDataModels.TerminMitBenutzer> {
        return this.config$.pipe(
            take(1),
            switchMap((config) => this.http.get<MainDataModels.TerminMitBenutzer>(`${config.termUrl}/GetTermin?calendarID=${calendarId}`, this.httpOptions)),
        );
    }

    saveAppointment(appointmentWithUsers: MainDataModels.TerminMitBenutzer): Observable<MainDataModels.TerminMitBenutzer> {
        return this.config$.pipe(
            take(1),
            switchMap((config) =>
                this.http.post<MainDataModels.TerminMitBenutzer>(`${config.termUrl}/UploadTermin`, this.stampAppointment(appointmentWithUsers), this.httpOptions),
            ),
            tap(() => this.triggerReload()),
        );
    }

    moveAppointment(move: MainDataModels.TerminVerschiebung, confirm: () => Promise<boolean>): Observable<"moved" | "cancelled"> {
        return this.getAppointmentById(move.Id).pipe(
            switchMap((termin) => (termin.groupID ? from(confirm()) : of(true))),
            switchMap((confirmed) => (confirmed ? this.postMove(move).pipe(map(() => "moved" as const)) : of("cancelled" as const))),
            tap({ next: () => this.triggerReload(), error: () => this.triggerReload() }),
        );
    }

    private postMove(move: MainDataModels.TerminVerschiebung): Observable<unknown> {
        return this.config$.pipe(
            take(1),
            switchMap((config) => this.http.post(`${config.termUrl}/MoveTermin`, move, this.httpOptions)),
        );
    }

    deleteAppointment(id: string): Observable<unknown> {
        return this.config$.pipe(
            take(1),
            switchMap((config) =>
                this.http.post(`${config.termUrl}/DeleteTermin`, new FormData(), {
                    headers: new HttpHeaders({ Authorization: this.helper.getSwissAuthorization(this.auth.currentUserInfo) }),
                    params: new HttpParams().set("id", id),
                }),
            ),
            tap(() => this.triggerReload()),
        );
    }

    rememberLastAppointment(calendarId: string): void {
        this.storage.add(KEY_LAST_APPOINTMENT, calendarId, STORAGE_DB);
    }

    /**
     * Builds the iCal recurrence rule for an appointment series, mirroring the
     * logic of the Navi 10 desktop client.
     */
    getRecurrenceRule(appointment: MainDataModels.Termin, series: MainDataModels.TerminSerie): string {
        let byDay = "";
        if (series.dayType && series.dayType !== 0) {
            byDay = this.getByDay(series.dayType);
        }

        let rule = "FREQ=";
        switch (series.recurrenceType) {
            case 0: {
                rule += "DAILY;" + byDay;
                if (series.ordinalDay && series.ordinalDay > 0) {
                    rule += "INTERVAL=" + series.ordinalDay + ";";
                }
                break;
            }
            case 1: {
                rule += "WEEKLY;" + byDay;
                if (series.ordinalWeek && series.ordinalWeek > 0) {
                    rule += "INTERVAL=" + series.ordinalWeek + ";";
                }
                break;
            }
            case 2: {
                rule += "MONTHLY;" + byDay;
                if (series.day && series.day > 0) {
                    rule += "BYMONTHDAY=" + series.day + ";";
                }
                if (series.ordinalMonth && series.ordinalMonth > 0) {
                    rule += "INTERVAL=" + series.ordinalMonth + ";";
                }
                break;
            }
            case 3: {
                rule += "MONTHLY;" + byDay;
                if (series.ordinalNumber && series.ordinalNumber > 0) {
                    rule += series.ordinalNumber === 5 ? "BYSETPOS=-1;" : "BYSETPOS=" + series.ordinalNumber + ";";
                }
                if (series.ordinalMonth && series.ordinalMonth > 0) {
                    rule += "INTERVAL=" + series.ordinalMonth + ";";
                }
                break;
            }
            case 4: {
                this.logError("getRecurrenceRule", `unbekannte Termin-Serie: ${series.recurrenceType} / Termin-Id: ${appointment.id}`);
                break;
            }
            case 5: {
                rule += "YEARLY;";
                if (series.day > 0) {
                    rule += "BYMONTHDAY=" + series.day + ";";
                }
                if (series.month > 0) {
                    rule += "BYMONTH=" + series.month + ";";
                }
                rule += "INTERVAL=1;";
                break;
            }
        }

        if (series.occurrences && series.occurrences > 0) {
            rule += "COUNT=" + series.occurrences + ";";
        } else if (series.endDate) {
            const end = this.timezone.add(new Date(series.endDate), "Europe/Zurich");
            // Year 9900 represents "never" in Navi 10 and is mapped to a finite count.
            rule += end.getFullYear() < 9900 ? "UNTIL=" + this.dateFormatPipe.getSyncfusionCalendarDateStr(end) : "COUNT=100;";
        }

        return rule;
    }

    private stampAppointment(appointmentWithUsers: MainDataModels.TerminMitBenutzer): MainDataModels.TerminMitBenutzer {
        const user = this.auth.getCurrentAuthenticatedUser()!;
        const storeInOL = (appointmentWithUsers.termin as { storeInOL?: unknown }).storeInOL;
        appointmentWithUsers.termin.storeInOL = storeInOL === 1 || storeInOL === true || storeInOL === "1" ? 1 : 0;
        if (appointmentWithUsers.istNeuerTermin) {
            appointmentWithUsers.termin.createdBy = user.loginname;
        } else {
            appointmentWithUsers.termin.modifiedBy = user.loginname;
        }
        return appointmentWithUsers;
    }

    private getByDay(dayType: number): string {
        if (dayType === 256) {
            return "BYDAY=SU,MO,TU,WE,TH,FR,SA;";
        }

        const days: Array<[number, string]> = [
            [64, "SU"],
            [32, "SA"],
            [16, "FR"],
            [8, "TH"],
            [4, "WE"],
            [2, "TU"],
            [1, "MO"],
        ];
        const selected: string[] = [];
        let remaining = dayType;
        for (const [bit, day] of days) {
            if (remaining >= bit) {
                selected.push(day);
                remaining -= bit;
            }
        }
        return "BYDAY=" + selected.join(",") + ";";
    }

    private loadStoredSelection(): Observable<string[]> {
        return from(this.storage.get<string[] | false>(KEY_SELECTED_EMPLOYEES, STORAGE_DB)).pipe(
            switchMap((stored) => (stored !== false ? of(stored) : this.defaultSelection())),
            catchError(() => of<string[]>([])),
        );
    }

    private defaultSelection(): Observable<string[]> {
        return this.getEmployees().pipe(
            map((employees) => {
                const user = this.auth.getCurrentAuthenticatedUser();
                const own = employees.find((employee) => employee.fK_EmployeeID === user?.employeeID);
                return own?.userID ? [own.loginname] : [];
            }),
            catchError(() => of<string[]>([])),
        );
    }

    private cached<T>(key: string, fallback: T): Observable<T> {
        return from(this.storage.get<T | false>(key, STORAGE_DB)).pipe(
            map((value) => (value === false || value === undefined || value === null ? fallback : (value as T))),
            catchError(() => of(fallback)),
        );
    }

    private get httpOptions() {
        return this.helper.getSwissHttpOptions(this.auth.currentUserInfo);
    }

    /** Central error logger for this service. */
    private logError(context: string, error: unknown): void {
        console.error(`calendar.service.ts; ${context}();`, error);
    }
}
